// GAP-15c — Bankroll declaration step (OddsJam Wave 1).
//
// Reuses the EXISTING declare_bankroll RPC (no new migration). Same
// regression-safe pattern as GAP-15b: standalone auth-gated route,
// deliberately NOT in steps.ts — it's user config/personalisation,
// not a legal/safety gate, so AuthGate's mandatory machinery stays
// byte-untouched. Currency locked to GHS in Phase 1A (mirrors
// safe-mode). Feeds Kelly sizing (GAP-03) + Sika readiness.
//
// Honest scope: we capture + confirm the declared bankroll. We do
// NOT invent a "recommended tier" from made-up thresholds — that
// would be a fabricated prescription (the doc asks for it but no
// real banding model exists; deferred, not faked).

import { useState } from "react";

import { getBrowserClient } from "../../supabase/browser.ts";

type SaveState = "idle" | "saving" | "saved" | "error";

const WRAP: React.CSSProperties = {
  maxWidth: 560,
  margin: "0 auto",
  padding: "32px 24px 80px",
  color: "var(--se-text, #fff)",
  fontFamily: "var(--se-font-body, system-ui)",
};

export function BankrollDeclarationPage(): JSX.Element {
  const [amount, setAmount] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [state, setState] = useState<SaveState>("idle");

  const parsed = Number(amount);
  const valid = Number.isFinite(parsed) && parsed > 0 && confirmed;

  async function submit(): Promise<void> {
    if (!valid) return;
    setState("saving");
    try {
      const client = getBrowserClient();
      const { error } = await (client.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: unknown }>)(
        "declare_bankroll",
        {
          p_amount: parsed,
          p_currency: "GHS",
          p_separation_confirmed: true,
        },
      );
      setState(error ? "error" : "saved");
    } catch {
      setState("error");
    }
  }

  return (
    <div data-testid="bankroll-declaration" style={WRAP}>
      <div
        style={{
          fontFamily: "var(--se-font-mono, monospace)",
          fontSize: 10,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "var(--se-text-muted, #888)",
        }}
      >
        Personalise · optional
      </div>
      <h1 style={{ fontSize: 24, fontWeight: 500, margin: "10px 0 6px" }}>
        Declare your bankroll
      </h1>
      <p
        style={{
          fontSize: 13,
          color: "var(--se-text-sec, #aaa)",
          lineHeight: 1.55,
          margin: "0 0 24px",
        }}
      >
        The capital you have set aside for betting — separate from money
        you need. STRIKZA uses it to size stakes (fractional Kelly) and
        to gate Sika plans. It never gates access and you can change it
        anytime.
      </p>

      <label
        style={{
          display: "block",
          fontSize: 12,
          color: "var(--se-text-muted, #888)",
          marginBottom: 6,
        }}
      >
        Bankroll amount (GHS)
      </label>
      <input
        data-testid="bankroll-amount"
        type="number"
        min={1}
        inputMode="decimal"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        placeholder="e.g. 500"
        style={{
          width: "100%",
          padding: "11px 13px",
          borderRadius: 3,
          border: "1px solid var(--se-border, #2a2a2a)",
          background: "var(--se-surface-raised, #1a1a1a)",
          color: "var(--se-text, #fff)",
          fontFamily: "var(--se-font-mono, monospace)",
          fontSize: 15,
          marginBottom: 18,
        }}
      />

      <label
        style={{
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
          fontSize: 13,
          color: "var(--se-text-sec, #aaa)",
          lineHeight: 1.5,
          cursor: "pointer",
          marginBottom: 22,
        }}
      >
        <input
          data-testid="bankroll-separation"
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        <span>
          This is money I can afford to lose, kept separate from money I
          need for living, debts, or others.
        </span>
      </label>

      <button
        type="button"
        data-testid="bankroll-submit"
        disabled={!valid || state === "saving" || state === "saved"}
        onClick={() => void submit()}
        style={{
          padding: "11px 22px",
          borderRadius: 2,
          border: "none",
          background: !valid || state === "saving"
            ? "var(--se-surface-raised, #222)"
            : "var(--se-amber, #d4a017)",
          color: !valid || state === "saving"
            ? "var(--se-text-muted, #888)"
            : "var(--se-surface-page, #000)",
          fontSize: 13.5,
          fontWeight: 600,
          cursor: valid && state === "idle" ? "pointer" : "default",
        }}
      >
        {state === "saving"
          ? "Saving…"
          : state === "saved"
            ? "Bankroll declared ✓"
            : "Declare bankroll"}
      </button>

      {state === "saved" ? (
        <div
          data-testid="bankroll-result"
          style={{ marginTop: 14, fontSize: 13, color: "var(--se-text-sec, #aaa)" }}
        >
          Saved. Stake sizing and Sika readiness now use this figure.
        </div>
      ) : null}
      {state === "error" ? (
        <div
          data-testid="bankroll-error"
          style={{ marginTop: 14, fontSize: 13, color: "var(--se-drop-red, #c0392b)" }}
        >
          Could not save — sign in and try again.
        </div>
      ) : null}
    </div>
  );
}
